import { useEffect, useId, useRef, useState } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { styleReset, Window, WindowHeader, WindowContent, Button } from 'react95';
import original from 'react95/dist/themes/original';

import { LocaleProvider, useLocale } from './i18n/LocaleContext';
import useDraggable from './hooks/useDraggable';
import Tabs from './components/Tabs';
import Profile from './components/Profile';
import Experience from './components/Experience';
import Education from './components/Education';
import Certificates from './components/Certificates';
import AboutApp from './components/AboutApp';
import DownloadDialog from './components/DownloadDialog';

const GlobalStyles = createGlobalStyle`
  ${styleReset}

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html, body, #root {
    height: 100%;
  }

  body {
    font-family: 'MS Sans Serif', Tahoma, sans-serif;
    margin: 0;
    padding: 0;
    background-color: #008080;
    overflow: hidden;
  }

  #root {
    height: 100%;
  }

  .desktop {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background:
      radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.06), transparent 40%),
      #008080;
  }

  .resume-window {
    position: absolute;
    width: min(900px, calc(100% - 32px));
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 48px);
    z-index: 1;
  }

  .resume-window.is-dragging {
    user-select: none;
  }

  .window-drag-handle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: grab;
    touch-action: none;
  }

  .resume-window.is-dragging .window-drag-handle {
    cursor: grabbing;
  }

  .resume-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding-bottom: 12px !important;
  }

  .tab-panel {
    height: min(520px, calc(100vh - 220px));
    overflow-y: auto;
    padding-right: 4px;
  }

  .tabs-toolbar {
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px !important;
  }

  .tabs-item-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .settings-menu {
    position: relative;
    display: inline-block;
  }

  .settings-dropdown {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    z-index: 20;
    min-width: 160px;
  }

  .download-overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    padding: 16px;
  }

  .download-dialog {
    width: min(360px, 100%);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  @media (max-width: 768px) {
    body {
      overflow: auto;
    }

    .desktop {
      min-height: 100%;
      height: auto;
      padding: 12px 8px;
    }

    .resume-window {
      position: relative;
      left: auto !important;
      top: auto !important;
      width: 100%;
      max-height: none;
      min-height: calc(100vh - 24px);
    }

    .window-drag-handle {
      cursor: default;
      touch-action: auto;
    }

    .tab-panel {
      height: auto;
      flex: 1;
      min-height: 0;
    }

    .window-title {
      font-size: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: calc(100% - 40px);
    }
  }
`;

const TAB_VIEWS = {
  main: Profile,
  experience: Experience,
  education: Education,
  certificates: Certificates,
  aboutApp: AboutApp,
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 769px)');
    const onChange = (event) => setIsDesktop(event.matches);

    setIsDesktop(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
};

const ResumeApp = () => {
  const [activeTab, setActiveTab] = useState('main');
  const [isDownloading, setIsDownloading] = useState(false);
  const panelRef = useRef(null);
  const desktopRef = useRef(null);
  const statusId = useId();
  const isDesktop = useIsDesktop();
  const { t, resumeData, lang } = useLocale();
  const ActiveView = TAB_VIEWS[activeTab];
  const activeLabel = t.tabs[activeTab] ?? '';

  const { nodeRef, position, isDragging, onHandlePointerDown } = useDraggable({
    enabled: isDesktop,
    boundsRef: desktopRef,
  });

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [activeTab, lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <>
      <GlobalStyles />
      <div className="desktop" ref={desktopRef} aria-label={t.desktopLabel}>
        <Window
          ref={nodeRef}
          className={`resume-window${isDragging ? ' is-dragging' : ''}`}
          role="application"
          aria-label={t.appLabel}
          style={
            isDesktop
              ? {
                  left: position.x,
                  top: position.y,
                }
              : undefined
          }
        >
          <WindowHeader
            className="window-drag-handle"
            onPointerDown={onHandlePointerDown}
          >
            <span className="window-title" id="app-title">
              Slatimov_Andrey.exe
            </span>
            <Button size="sm" aria-label={t.close} disabled>
              <span aria-hidden="true">×</span>
            </Button>
          </WindowHeader>

          <Tabs activeTab={activeTab} onChange={setActiveTab} />

          <WindowContent className="resume-content">
            <div
              ref={panelRef}
              className="tab-panel"
              role="tabpanel"
              id={`panel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
              tabIndex={0}
            >
              <ActiveView data={resumeData} />
            </div>

            <p id={statusId} className="sr-only" aria-live="polite">
              {t.sectionOpened}: {activeLabel}
            </p>

            <Button
              fullWidth
              style={{ marginTop: '16px', flexShrink: 0 }}
              onClick={() => setIsDownloading(true)}
              disabled={isDownloading}
            >
              {t.download}
            </Button>
          </WindowContent>
        </Window>

        <DownloadDialog
          open={isDownloading}
          onClose={() => setIsDownloading(false)}
        />
      </div>
    </>
  );
};

const App = () => (
  <ThemeProvider theme={original}>
    <LocaleProvider>
      <ResumeApp />
    </LocaleProvider>
  </ThemeProvider>
);

export default App;
